/**
 * NotificationItem.tsx
 *
 * Single notification row used in both the dropdown and the /notifications page.
 * Handles grouped notifications (count > 1) by showing a multi-avatar stack
 * and the aggregated message written by the server.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { Avatar, AvatarGroup, Box, Typography } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import PaidIcon from '@mui/icons-material/Paid';
import ReplyIcon from '@mui/icons-material/Reply';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import NotificationsIcon from '@mui/icons-material/Notifications';
import type { Notification, NotificationType } from '../../services/api/notificationApi';

// ── Styled ────────────────────────────────────────────────────────────────────

const Row = styled('button')<{ $unread: boolean }>`
  all: unset;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-3);
  padding: var(--spacing-3) var(--spacing-4);
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
  background: ${({ $unread }) =>
    $unread ? 'color-mix(in srgb, var(--color-primary) 6%, transparent)' : 'transparent'};
  border-left: 3px solid ${({ $unread }) => ($unread ? 'var(--color-primary)' : 'transparent')};
  transition: background var(--duration-shortest) ease;
  text-align: left;

  &:hover {
    background: var(--color-bg-hover);
  }
`;

const TypeIconWrap = styled(Box)<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: var(--radius-full);
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  svg {
    font-size: 17px;
    color: #fff;
  }
`;

const SingleAvatar = styled(Avatar)`
  width: 36px !important;
  height: 36px !important;
  font-size: 13px !important;
  flex-shrink: 0;
`;

const SmallTypeChip = styled(Box)<{ $color: string }>`
  width: 18px;
  height: 18px;
  border-radius: var(--radius-full);
  background: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  svg {
    font-size: 10px;
    color: #fff;
  }
`;

const Message = styled(Typography)`
  font-size: 13px !important;
  line-height: 1.45 !important;
  color: var(--color-text-primary);
`;

const TimeStamp = styled(Typography)`
  font-size: 11px !important;
  color: var(--color-text-disabled);
  margin-top: 2px !important;
`;

// ── Config ────────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<NotificationType, { icon: React.ReactNode; color: string }> = {
  JOURNAL_LIKE: { icon: <FavoriteIcon />, color: '#e91e8c' },
  JOURNAL_COMMENT: { icon: <ChatBubbleOutlineIcon />, color: '#1976d2' },
  JOURNAL_REPOST: { icon: <RepeatIcon />, color: '#2e7d32' },
  JOURNAL_BOOKMARK: { icon: <BookmarkBorderIcon />, color: '#5c35b5' },
  NEW_FOLLOWER: { icon: <PersonAddIcon />, color: '#7b1fa2' },
  NEW_JOURNAL_FROM_FOLLOWING: { icon: <AutoStoriesIcon />, color: '#f57c00' },
  TIP_RECEIVED: { icon: <PaidIcon />, color: '#d4af37' },
  COMMENT_REPLY: { icon: <ReplyIcon />, color: '#0288d1' },
  MENTION: { icon: <AlternateEmailIcon />, color: '#c62828' },
  NEW_SUBSCRIBER: { icon: <CardMembershipIcon />, color: '#00695c' },
  SUBSCRIPTION_EXPIRING: { icon: <CardMembershipIcon />, color: '#e65100' },
  SYSTEM: { icon: <NotificationsIcon />, color: '#546e7a' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Component ─────────────────────────────────────────────────────────────────

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: number, ids: number[]) => Promise<void>;
  onNavigate?: () => void;
}

export default function NotificationItem({
  notification,
  onRead,
  onNavigate,
}: NotificationItemProps) {
  const navigate = useNavigate();
  const config = TYPE_CONFIG[notification.type] ?? TYPE_CONFIG.SYSTEM;
  const isGrouped = notification.count > 1;

  const handleClick = async () => {
    if (!notification.isRead) {
      await onRead(notification.id, notification.ids);
    }
    onNavigate?.();
    if (notification.actionUrl) {
      try {
        const url = new URL(notification.actionUrl);
        navigate(url.pathname + url.hash);
      } catch {
        navigate(notification.actionUrl);
      }
    }
  };

  return (
    <Row $unread={!notification.isRead} onClick={handleClick}>
      {/* Left side: avatar(s) or type icon */}
      {isGrouped ? (
        /* Grouped: MUI AvatarGroup — container must be wide enough for two overlapping avatars */
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', width: 52 }}>
          <AvatarGroup
            max={2}
            sx={{
              '& .MuiAvatar-root': {
                width: 28,
                height: 28,
                fontSize: 11,
                border: '2px solid var(--color-bg-surface)',
              },
            }}
          >
            {notification.actors.map((a) => (
              <Avatar key={a.id} src={a.avatar ?? undefined}>
                {(a.displayName ?? a.username)[0].toUpperCase()}
              </Avatar>
            ))}
          </AvatarGroup>
        </Box>
      ) : notification.actors[0] ? (
        /* Single actor: their avatar */
        <SingleAvatar src={notification.actors[0].avatar ?? undefined}>
          {(notification.actors[0].displayName ?? notification.actors[0].username)[0].toUpperCase()}
        </SingleAvatar>
      ) : (
        /* No actor (system / subscription): type icon */
        <TypeIconWrap $color={config.color}>{config.icon}</TypeIconWrap>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Message noWrap={false}>{notification.message}</Message>
        <TimeStamp>{formatRelativeTime(notification.createdAt)}</TimeStamp>
      </Box>

      {/* Small type chip on the right when we have an actor avatar */}
      {notification.actors.length > 0 && (
        <SmallTypeChip $color={config.color}>
          {React.cloneElement(config.icon as React.ReactElement)}
        </SmallTypeChip>
      )}
    </Row>
  );
}
